/**
 * Pure, unit-tested helpers behind the catalog list filters. Kept out of the
 * page component so the predicate logic is testable in isolation.
 */
import type { ProductSummary } from "../../api/catalog";
import { formatMoney } from "../../lib/format";

/**
 * Price-range predicate over the per-company resolved gross already carried on
 * the list payload (ProductSummary.listPrice). Empty or non-numeric bounds are
 * ignored; once a finite bound is active a null price drops out (a priceless
 * product can't sit inside a range), mirroring the protein-range guard.
 * Bounds are inclusive.
 */
export function matchesPriceRange(listPrice: number | null, min: string, max: string): boolean {
  const lo = min.trim() === "" ? null : Number(min);
  const hi = max.trim() === "" ? null : Number(max);
  if (lo != null && Number.isFinite(lo) && (listPrice == null || listPrice < lo)) return false;
  if (hi != null && Number.isFinite(hi) && (listPrice == null || listPrice > hi)) return false;
  return true;
}

/**
 * Normalize a free-typed price bound: keep digits, treat any comma as a decimal
 * point, and collapse to at most ONE separator. Guarantees the stored string is
 * always either empty or a parseable number, so the filter, the FilterGroup badge
 * and the active-filter chip can never disagree (no "active" indicator that filters
 * nothing). Mid-typing values like "1." are preserved so decimals type naturally.
 */
export function sanitizePriceInput(raw: string): string {
  const cleaned = raw.replace(/[^0-9.,]/g, "").replace(/,/g, ".");
  const firstDot = cleaned.indexOf(".");
  if (firstDot === -1) return cleaned;
  return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, "");
}

/** Currency formatting for catalog prices — single source instead of inline Intl. */
export function formatPrice(value: number, currency: string | null, fractionDigits = 2): string {
  return formatMoney(value, currency, undefined, { maximumFractionDigits: fractionDigits });
}

/** Display currency for a product set — first product that declares one, else EUR. */
export function listCurrency(items: ProductSummary[]): string {
  return items.find((p) => p.currency)?.currency ?? "EUR";
}
